import React, { useEffect, useRef, useState } from "react";
import { useDAWStore, INSTRUMENT_DEFS } from "../../store/dawStore";
import engine from "../../audio/AudioEngine";
import { subscribeKitSoundLibrary, refreshKitSoundLibrary } from "../../audio/kitSoundLibrary";
import Knob from "./Knob";
import { UploadSimple, X, Play as PlayIcon, MusicNotes, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const COLOR = "#EAB308";

export default function Sampler() {
  const { tracks, selectedTrackId, setTrackSample, selectTrack, updateTrack, setTrackSamplerError } = useDAWStore();
  const samplerTracks = tracks.filter((t) => t.type === "sampler");
  const activeTrack = tracks.find((t) => t.id === selectedTrackId && t.type === "sampler") || samplerTracks[0];

  if (samplerTracks.length === 0) {
    return (
      <div className="plugin-panel p-5 h-full overflow-auto flex items-center justify-center" data-testid="plugin-sampler">
        <div className="text-center text-white/50 text-sm">
          No sampler tracks. Drag <span className="text-white/80">Sampler</span> from the sidebar onto a track header.
        </div>
      </div>
    );
  }

  return (
    <div className="plugin-panel p-5 h-full overflow-auto" data-testid="plugin-sampler">
      <div className="flex items-center justify-between mb-5 pb-3 border-b border-[#262626]">
        <div className="flex items-center gap-3">
          <div className="w-1 h-7 rounded-sm" style={{ background: COLOR, boxShadow: `0 0 8px ${COLOR}80` }} />
          <h3 className="font-display font-bold text-base tracking-tight">Sampler</h3>
        </div>
        <select
          value={activeTrack?.id || ""}
          onChange={(e) => selectTrack(e.target.value)}
          className="bg-[#0a0a0a] border border-[#222] rounded px-2 py-1 text-[11px] font-mono focus:outline-none focus:border-[#444]"
          data-testid="sampler-track-select"
        >
          {samplerTracks.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>

      {activeTrack && <SamplerPad track={activeTrack} setTrackSample={setTrackSample} updateTrack={updateTrack} setTrackSamplerError={setTrackSamplerError} />}
    </div>
  );
}

function SamplerPad({ track, setTrackSample, updateTrack, setTrackSamplerError }) {
  const setTrackGlide = useDAWStore((s) => s.setTrackGlide);
  const inputRef = useRef(null);
  const [hover, setHover] = React.useState(false);
  // Kit Sound Library: sounds pulled from the admin's Training Lab kits.
  // Grouped in the dropdown by kit (Drum / Bass / Melody) so the producer can
  // scan by family. Selection is instant: engine.loadSampleToTrack() rebuilds
  // the sampler with the picked sound at its declared root pitch, and the
  // store persists samplerOneShot / samplerRootPitch on the track.
  const [kitSounds, setKitSounds] = useState([]);
  const [loadingLib, setLoadingLib] = useState(true);

  // v19.7 — Subscribe to the single module-level kit library store.
  // The subscriber callback fires:
  //   • immediately with the current snapshot when we mount (if a
  //     fetch has already happened globally),
  //   • again whenever any Training-Ground CRUD op broadcasts via
  //     the kitsBus (add / delete / rename / kit-delete / set-default).
  // This replaces the prior effect-driven `refreshLibrary()` chain
  // whose `setKitSounds` was being lost on strict-mode remounts and
  // post-`setTrackSample` re-renders (see /app/test_reports/iteration_46).
  useEffect(() => {
    const unsub = subscribeKitSoundLibrary((sounds) => {
      setKitSounds(sounds || []);
      setLoadingLib(false);
    });
    return unsub;
  }, []);

  const refreshLibrary = React.useCallback(async () => {
    setLoadingLib(true);
    try {
      const sounds = await refreshKitSoundLibrary();
      return sounds;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[Sampler] failed to refresh kit sound library", e);
      return [];
    } finally {
      setLoadingLib(false);
    }
  }, []);

  // v19.7 — The mount effect no longer manages the sound library
  // (that's the shared subscriber above). It ONLY handles the
  // auto-load-a-default-sound behaviour so the sampler never opens
  // completely empty for a freshly-created sampler track.
  const autoLoadedRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sounds = await refreshLibrary();
      if (cancelled || autoLoadedRef.current) return;
      if (track.sampleUrl) return; // producer already picked something
      const pickOrder = [
        (s) => s.kit === "melody" && s.slot === "piano",
        (s) => s.kit === "melody",
        (s) => s.kit === "bass",
        (s) => s.kit === "drum" && s.slot === "kick",
        (s) => s.kit === "drum",
      ];
      let choice = null;
      for (const fn of pickOrder) {
        choice = sounds.find(fn);
        if (choice) break;
      }
      if (!choice) return;
      autoLoadedRef.current = true;
      setTrackSample(track.id, choice.url, choice.label, {
        samplerRootPitch: choice.rootPitch,
        samplerOneShot: choice.oneShot,
        samplerSourceKit: choice.kit,
      });
      const result = await engine.loadSampleToTrack(track.id, choice.url, choice.rootPitch);
      if (result?.ok) {
        toast.success(`Auto-loaded ${choice.label} · pick another from the Kit Library`);
      } else {
        setTrackSamplerError(track.id, result?.reason || "Sample corrupted");
      }
    })();
    return () => { cancelled = true; };
    // Rerun when the active track changes so switching between sampler tracks
    // still auto-loads a default on the newly-selected one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track.id]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("audio/") && !/\.(wav|mp3|ogg|aac|m4a|flac)$/i.test(file.name)) {
      toast.error("Drop an audio file (wav, mp3, ogg, etc)");
      return;
    }
    // v19.11 — Persist to Object Storage FIRST. The prior code wrapped
    // the File in URL.createObjectURL() which dies on page reload;
    // auto-save captured that dead blob:// URL and reopened projects
    // played silence. Now we POST /api/user/sampler-uploads and only
    // set the track's sampleUrl to the persistent /api/user-samples/…
    // URL returned by the backend. If the upload fails (offline, quota,
    // etc) we fall back to the blob URL for THIS session so playback
    // still works — but we surface a toast so the producer knows the
    // sample won't survive a reload and can re-try.
    let persistentUrl = null;
    let persistentFilename = file.name;
    try {
      const form = new FormData();
      form.append("file", file);
      const token = localStorage.getItem("crow_token");
      const uploadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/user/sampler-uploads`;
      const resp = await fetch(uploadUrl, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      if (resp.ok) {
        const data = await resp.json();
        persistentUrl = data.url;
        persistentFilename = data.filename || file.name;
      } else {
        // eslint-disable-next-line no-console
        console.warn("[Sampler] user-upload persistence failed:", resp.status, await resp.text());
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[Sampler] user-upload persistence exception:", e);
    }
    if (!persistentUrl) {
      toast.error("Couldn't save sample to the cloud — it will play now but won't survive a reload. Please try again once you're back online.");
    }
    const url = persistentUrl || URL.createObjectURL(file);
    setTrackSample(track.id, url, persistentFilename, { samplerRootPitch: "C4", samplerOneShot: false, samplerSourceKit: "upload" });
    const result = await engine.loadSampleToTrack(track.id, url, "C4");
    if (result?.ok) {
      toast.success(`Loaded ${persistentFilename}`);
    } else {
      setTrackSamplerError(track.id, result?.reason || "Sample corrupted — please re-upload");
      toast.error(`${persistentFilename}: ${result?.reason || "decode failed"}`);
    }
  };

  const pickFromLibrary = async (soundId) => {
    if (!soundId) return;
    const sound = kitSounds.find((s) => s.id === soundId);
    if (!sound) return;
    setTrackSample(track.id, sound.url, sound.label, {
      samplerRootPitch: sound.rootPitch,
      samplerOneShot: sound.oneShot,
      samplerSourceKit: sound.kit,
    });
    const result = await engine.loadSampleToTrack(track.id, sound.url, sound.rootPitch);
    if (result?.ok) {
      toast.success(`Loaded ${sound.label}`);
    } else {
      // Corrupted seeded sample (e.g. the known bad bass C1.wav) — surface a
      // clear message and let the fallback Galaxy Synth voice keep playback
      // audible while the admin re-uploads.
      setTrackSamplerError(track.id, result?.reason || "Sample corrupted — please re-upload");
      toast.error(`${sound.label}: ${result?.reason || "decode failed"} — using Galaxy Synth`);
    }
  };

  const handleClear = () => {
    setTrackSample(track.id, null, "", { samplerRootPitch: "C4", samplerOneShot: false, samplerSourceKit: "upload" });
    setTrackSamplerError(track.id, null);
    toast.info("Sample cleared");
  };

  // Group sounds by kit so the <optgroup>s make the dropdown scannable.
  const drumSounds   = kitSounds.filter((s) => s.kit === "drum");
  const bassSounds   = kitSounds.filter((s) => s.kit === "bass");
  const melodySounds = kitSounds.filter((s) => s.kit === "melody");
  const selectedSoundId = kitSounds.find((s) => s.url === track.sampleUrl)?.id || "";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Drop zone */}
      <div className="lg:col-span-2 flex flex-col gap-3">
        {/* Corrupted-sample banner: shown when the last load attempt failed.
            The trigger paths in AudioEngine.js silently fall back to a per-
            track Galaxy Synth voice, but the producer needs to know that the
            sample they hear is a synth stand-in — not the file they picked. */}
        {track.samplerLoadError && (
          <div
            className="rounded-lg px-3 py-2 flex items-center gap-2 text-xs font-mono"
            style={{ background: "#2a0f0f", border: "1px solid #dc2626", color: "#fecaca" }}
            data-testid="sampler-load-error"
          >
            <span style={{ color: "#dc2626", fontSize: 14, fontWeight: 800 }}>⚠</span>
            <span className="flex-1">{track.samplerLoadError} · Galaxy Synth fallback in use</span>
          </div>
        )}
        {/* Kit Sound Library dropdown — the star of this feature */}
        <div className="rounded-lg p-3 flex items-center gap-2" style={{ background: "#0f0f0f", border: "1px solid #262626" }}>
          <MusicNotes size={16} weight="fill" style={{ color: COLOR }} />
          <div className="flex-1 min-w-0">
            <div className="label-mono mb-1 flex items-center justify-between gap-2">
              <span>Kit Sound Library</span>
              <button
                onClick={refreshLibrary}
                className="text-white/40 hover:text-white/80 transition"
                title="Refresh library from Training Lab"
                data-testid="sampler-library-refresh"
                disabled={loadingLib}
              >
                <ArrowClockwise size={12} weight="bold" className={loadingLib ? "animate-spin" : ""} />
              </button>
            </div>
            <select
              value={selectedSoundId}
              onChange={(e) => pickFromLibrary(e.target.value)}
              className="w-full bg-[#080808] border border-[#2a2a2a] rounded px-2 py-1.5 text-xs font-mono focus:outline-none focus:border-[#444]"
              data-testid="sampler-library-select"
            >
              <option value="">— pick a sound from your training kits —</option>
              {drumSounds.length > 0 && (
                <optgroup label={`Drum Kit (${drumSounds.length})`}>
                  {drumSounds.map((s) => (
                    <option key={s.id} value={s.id} data-testid={`sampler-library-opt-${s.id}`}>
                      {s.label}
                    </option>
                  ))}
                </optgroup>
              )}
              {bassSounds.length > 0 && (
                <optgroup label={`Bass Kit (${bassSounds.length})`}>
                  {bassSounds.map((s) => (
                    <option key={s.id} value={s.id} data-testid={`sampler-library-opt-${s.id}`}>
                      {s.label}
                    </option>
                  ))}
                </optgroup>
              )}
              {melodySounds.length > 0 && (
                <optgroup label={`Melody Kit (${melodySounds.length})`}>
                  {melodySounds.map((s) => (
                    <option key={s.id} value={s.id} data-testid={`sampler-library-opt-${s.id}`}>
                      {s.label}
                    </option>
                  ))}
                </optgroup>
              )}
              {kitSounds.length === 0 && loadingLib && (
                <option value="" disabled>Loading kit library…</option>
              )}
              {kitSounds.length === 0 && !loadingLib && (
                <option value="" disabled>No sounds uploaded yet — visit the Training Lab</option>
              )}
            </select>
          </div>
        </div>

        {/* Drop zone (kept for local files) */}
        <div
          className={`relative rounded-lg p-8 border-2 transition-all cursor-pointer min-h-[200px] flex flex-col items-center justify-center gap-3 ${hover ? "scale-[1.01]" : ""}`}
          style={{
            borderColor: track.sampleUrl ? track.color : (hover ? COLOR : "#2a2a2a"),
            borderStyle: track.sampleUrl ? "solid" : "dashed",
            background: track.sampleUrl
              ? `linear-gradient(180deg, ${track.color}20 0%, #161616 100%)`
              : (hover ? `${COLOR}15` : "linear-gradient(180deg, #1a1a1a, #0f0f0f)"),
            boxShadow: track.sampleUrl
              ? `inset 0 1px 0 rgba(255,255,255,0.1), 0 0 16px ${track.color}25`
              : "inset 0 1px 0 rgba(255,255,255,0.05), 0 4px 12px rgba(0,0,0,0.4)",
          }}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setHover(true); }}
          onDragLeave={() => setHover(false)}
          onDrop={(e) => { e.preventDefault(); setHover(false); handleFile(e.dataTransfer.files?.[0]); }}
          data-testid="sampler-dropzone"
        >
          <input
            ref={inputRef}
            type="file"
            accept="audio/*,.wav,.mp3,.ogg,.flac,.m4a"
            className="hidden"
            onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }}
          />
          {!track.sampleUrl ? (
            <>
              <UploadSimple size={36} weight="bold" className="text-white/40" />
              <div className="text-sm font-semibold text-white/70">Drop or click to load audio</div>
              <div className="label-mono">or pick a sound from your Training Lab kits ↑</div>
            </>
          ) : (
            <>
              <div className="text-xs font-mono text-white/40 uppercase tracking-wider flex items-center gap-2">
                Sample loaded
                {track.samplerSourceKit && track.samplerSourceKit !== "upload" && (
                  <span className="px-1.5 py-0.5 rounded-sm font-bold text-[9px]" style={{ background: `${COLOR}30`, color: COLOR }}>
                    {track.samplerSourceKit.toUpperCase()}
                  </span>
                )}
                <span className="px-1.5 py-0.5 rounded-sm font-bold text-[9px] bg-white/10 text-white/60">
                  {track.samplerOneShot ? "ONE-SHOT" : `PITCHED @${track.samplerRootPitch || "C4"}`}
                </span>
              </div>
              <div className="text-lg font-bold truncate max-w-full" style={{ color: track.color }} data-testid="sampler-loaded-name">{track.sampleName}</div>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={(e) => { e.stopPropagation(); engine.audition(track); }}
                  className="btn-3d-accent btn-3d px-4 py-2 text-xs font-bold flex items-center gap-2"
                  data-testid="sampler-audition-btn"
                >
                  <PlayIcon size={12} weight="fill" /> Audition
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleClear(); }}
                  className="btn-3d px-3 py-2 text-xs flex items-center gap-1.5"
                  data-testid="sampler-clear-btn"
                >
                  <X size={12} weight="bold" /> Clear
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Side controls */}
      <div className="flex flex-col gap-4 items-center">
        <div className="label-mono">Track Controls</div>
        <Knob
          value={track.volume ?? 0.8} min={0} max={1} step={0.01} size={56} label="VOLUME"
          accentColor={track.color}
          onChange={(v) => updateTrack(track.id, { volume: v })}
          format={(v) => `${Math.round(v * 100)}%`}
          data-testid="sampler-volume-knob"
        />

        <div className="border-t border-[#262626] w-full pt-3">
          <div className="label-mono text-center mb-2">ADSR Envelope</div>
          <SamplerAdsr track={track} />
        </div>

        {/* Glide (portamento) — smooth pitch slide between consecutive
            notes. Tone.Sampler.set({portamento}) ramps voice frequency
            over this many seconds. 0 = classic instantaneous note change,
            0.05-0.30s reads as a musical slide, higher is dramatic. */}
        {/* v12.0 GLIDE FIX — Tone.Sampler doesn't expose real portamento
            (each note pitch-shifts a static buffer, there's no oscillator
            to ramp). The knob is greyed out with an explanatory tooltip
            so producers aren't misled that it does anything. Real glide
            still works on Synth / Melody / Bass tracks. */}
        <div className="border-t border-[#262626] w-full pt-3" style={{ opacity: 0.35 }}>
          <div className="label-mono text-center mb-2">Glide</div>
          <div
            className="flex flex-col items-center gap-1"
            title="Glide only affects Synth / Bass / Melody tracks. Tone.Sampler doesn't support portamento — sample pitch-shift is stepped, not ramped."
          >
            <Knob
              value={0}
              min={0}
              max={2}
              step={0.005}
              size={44}
              label="TIME"
              accentColor="#555"
              onChange={() => {}}
              format={() => "n/a"}
              data-testid="sampler-glide-knob-disabled"
            />
            <div className="text-[9px] text-white/40 font-mono text-center">Sampler · not supported</div>
          </div>
        </div>

        {/* Root Pitch selector — for pitched (non-one-shot) samples, this is
            the note the sample was recorded at. Tone.Sampler pitch-shifts
            from here for every other note the piano roll requests. Correct
            root = correct MIDI-note-spectrum mapping. */}
        {!track.samplerOneShot && track.sampleUrl && (
          <div className="border-t border-[#262626] w-full pt-3">
            <div className="label-mono text-center mb-2">Root Pitch</div>
            <SamplerRootPitch track={track} />
          </div>
        )}

        <div className="text-center">
          <div className="label-mono mb-1">Pitch via Piano Roll</div>
          <div className="text-xs font-semibold text-white/80">Switch to Piano Roll ↓</div>
        </div>
      </div>
    </div>
  );
}

// Small select that lets producers correct the root pitch of a loaded
// sample. Rebuilding the Tone.Sampler with the new root is a one-liner —
// engine.loadSampleToTrack() clones the existing buffer at the new pitch.
function SamplerRootPitch({ track }) {
  const { setTrackSample, setTrackSamplerError } = useDAWStore();
  const rootPitch = track.samplerRootPitch || "C4";
  const [busy, setBusy] = React.useState(false);
  const pitches = [];
  for (let oct = 1; oct <= 6; oct++) {
    for (const n of ["C", "D", "E", "F", "G", "A", "B"]) pitches.push(`${n}${oct}`);
  }
  const onChange = async (e) => {
    const newRoot = e.target.value;
    if (!track.sampleUrl || newRoot === rootPitch) return;
    setBusy(true);
    // Dispose the current sampler so the new root cleanly reindexes the buffer
    try { engine.samplerPlayers?.[track.id]?.dispose(); } catch { /* noop */ }
    delete engine.samplerPlayers?.[track.id];
    const result = await engine.loadSampleToTrack(track.id, track.sampleUrl, newRoot);
    setTrackSample(track.id, track.sampleUrl, track.sampleName, {
      samplerRootPitch: newRoot,
      samplerOneShot: track.samplerOneShot,
      samplerSourceKit: track.samplerSourceKit,
    });
    if (!result?.ok) setTrackSamplerError(track.id, result?.reason || "Sample corrupted — please re-upload");
    setBusy(false);
  };
  return (
    <select
      value={rootPitch}
      onChange={onChange}
      disabled={busy}
      className="w-full bg-[#080808] border border-[#2a2a2a] rounded px-2 py-1 text-xs font-mono focus:outline-none focus:border-[#444]"
      data-testid="sampler-root-pitch"
    >
      {pitches.map((p) => <option key={p} value={p}>{p}</option>)}
    </select>
  );
}

function SamplerAdsr({ track }) {
  const { setSamplerEnv } = useDAWStore();
  const env = track.samplerEnv || { attack: 0.005, decay: 0.1, sustain: 1, release: 0.2 };

  const apply = (param, value) => {
    setSamplerEnv(track.id, { [param]: value });
    // For polyphonic Tone.Sampler, attack/release apply per-voice automatically;
    // setupSequencer() re-syncs these on the next tick. Decay/sustain are visual
    // only (Sampler uses the sample's natural envelope for sustain/decay).
    const sampler = engine.samplerPlayers?.[track.id];
    if (sampler) {
      try {
        if (param === "attack")  sampler.attack = value;
        if (param === "release") sampler.release = value;
      } catch {}
    }
  };

  // visual envelope shape
  const w = 180, h = 56;
  const ax = (env.attack / 2) * (w * 0.35);
  const dx = ax + (env.decay / 2) * (w * 0.3);
  const sx = dx + 36;
  const rx = sx + (env.release / 2) * (w * 0.35);

  return (
    <div className="flex flex-col items-center gap-3">
      <svg width={w} height={h} className="bg-[#0a0a0a] rounded border border-[#222]">
        <polyline
          points={`0,${h - 2} ${ax},4 ${dx},${h - env.sustain * (h - 8) - 4} ${sx},${h - env.sustain * (h - 8) - 4} ${rx},${h - 2}`}
          fill="none"
          stroke={COLOR}
          strokeWidth="2"
          style={{ filter: `drop-shadow(0 0 3px ${COLOR})` }}
        />
      </svg>
      <div className="grid grid-cols-4 gap-2">
        <Knob value={env.attack} min={0.001} max={2} step={0.005} size={38} label="ATK"
          accentColor={COLOR} onChange={(v) => apply("attack", v)}
          format={(v) => `${v.toFixed(2)}s`} data-testid="sampler-adsr-a" />
        <Knob value={env.decay} min={0.005} max={2} step={0.005} size={38} label="DEC"
          accentColor={COLOR} onChange={(v) => apply("decay", v)}
          format={(v) => `${v.toFixed(2)}s`} data-testid="sampler-adsr-d" />
        <Knob value={env.sustain} min={0} max={1} step={0.01} size={38} label="SUS"
          accentColor={COLOR} onChange={(v) => apply("sustain", v)}
          format={(v) => `${Math.round(v * 100)}%`} data-testid="sampler-adsr-s" />
        <Knob value={env.release} min={0.005} max={4} step={0.01} size={38} label="REL"
          accentColor={COLOR} onChange={(v) => apply("release", v)}
          format={(v) => `${v.toFixed(2)}s`} data-testid="sampler-adsr-r" />
      </div>
    </div>
  );
}

